import type { ConversationClient } from './messagerieClients'
/**
 * Les types du domaine Opérations.
 *
 * Ils ne décrivent pas les tables Supabase telles quelles : ils décrivent la
 * forme que `refresh()` donnait déjà aux données dans la version HTML, après
 * aplatissement des jointures et traduction des statuts. C'est cette forme que
 * les écrans consomment, et c'est elle qu'il faut typer — typer les tables
 * brutes aurait laissé la traduction hors du contrôle du compilateur.
 */

import type { DetailPrix, GrilleOperee, TypeVehicule } from './prixOpere'

/* ------------------------------------------------------------------ statuts */

/**
 * ⚠️ DEUX vocabulaires coexistent, et les confondre casse le cycle de vie.
 * L'interface et l'état local parlent français ; la base et les edge functions
 * parlent anglais. La version HTML gérait ça avec deux objets `ST_FR2DB` /
 * `ST_DB2FR` sans aucune garantie qu'ils restent en miroir. Ici le type dérive
 * de la table de correspondance : ajouter un statut d'un côté sans l'autre ne
 * compile pas.
 */
export const STATUT_VERS_BASE = {
  creee: 'created',
  assignee: 'assigned',
  collecte: 'pickup',
  en_livraison: 'delivering',
  livree: 'delivered',
  annulee: 'cancelled',
} as const

export type Statut = keyof typeof STATUT_VERS_BASE
export type StatutBase = (typeof STATUT_VERS_BASE)[Statut]

export const STATUT_DEPUIS_BASE = Object.fromEntries(
  Object.entries(STATUT_VERS_BASE).map(([fr, db]) => [db, fr]),
) as Record<StatutBase, Statut>

/** Libellé, pastille et transition suivante — transposé de `STATUS`. */
export const STATUTS: Record<Statut, { libelle: string; pastille: string; suivant: Statut | null }> = {
  creee: { libelle: 'Créée', pastille: 'b-gray', suivant: 'assignee' },
  assignee: { libelle: 'Assignée', pastille: 'b-blue', suivant: 'collecte' },
  collecte: { libelle: 'Collecte', pastille: 'b-gold', suivant: 'en_livraison' },
  en_livraison: { libelle: 'En livraison', pastille: 'b-info', suivant: 'livree' },
  livree: { libelle: 'Livrée', pastille: 'b-green', suivant: null },
  annulee: { libelle: 'Annulée', pastille: 'b-red', suivant: null },
}

/* ------------------------------------------------------------------- canaux */

export const CANAUX = {
  phone: { libelle: 'Appel', icone: 'phone' },
  whatsapp: { libelle: 'WhatsApp', icone: 'chat' },
  portal: { libelle: 'Portail', icone: 'globe' },
  other: { libelle: 'Autre', icone: 'more' },
} as const

export type Canal = keyof typeof CANAUX

/* ------------------------------------------------------------------ clients */

/** Un créneau de desserte. `weekday` suit la norme ISO : 1 = lundi, 7 = dimanche. */
export interface Creneau {
  weekday: number
  start_time: string
  end_time: string
  /** Identifiant en base (`client_time_slots.id`) — absent d'un créneau pas encore enregistré. */
  id?: string
}

/**
 * `pme` = client au forfait, desservi dans des créneaux convenus ; hors créneau
 * un supplément s'applique. `particulier` = client à la course, sans créneau.
 */
export type TypeClient = 'pme' | 'particulier'

/** `client_accounts.status` : un client en pause ou clos ne facture plus au-delà de la fin du mois en cours. */
export type StatutClient = 'active' | 'paused' | 'closed'

export interface Client {
  id: string
  type: TypeClient
  name: string
  contact_name: string | null
  phone: string | null
  email: string | null
  quartier: string | null
  commune: string | null
  notes: string | null
  /** Pause / clôture : le statut de la FICHE fait suivre l'abonnement (déclencheur, migration 0031). */
  status: StatutClient
  plan_code: string | null
  offslot_surcharge_fcfa: number
  default_pickup: string
  default_pickup_note: string
  /** Point de collecte habituel géolocalisé (`default_pickup_lat/lng`, 0002) — nul tant que la fiche n'a pas d'épingle. */
  default_pickup_lat: number | null
  default_pickup_lng: number | null
  /** Option mensuelle « longue distance » (0030) : km supplémentaires au tarif réduit. */
  option_longue_distance: boolean
  slots: Creneau[]
  /** Un compte est relié à la fiche (0045) : le client lit la messagerie dans l'app. Sinon, on lui écrit sur WhatsApp. */
  a_un_compte: boolean
  /** Statut juridique (0035), facultatif à la création d'une PME : complété à l'onboarding. Jamais lu depuis la liste. */
  statut_juridique?: 'societe' | 'entreprenant' | 'personne_physique' | null
}

/* ----------------------------------------------------------------- livreurs */

export type StatutLivreur = 'offline' | 'available' | 'on_course'

export interface Livreur {
  id: string
  full_name: string
  phone: string | null
  status: StatutLivreur
  rating: number
  /** Position poussée par l'app livreur (`drivers.lat` / `lng`, migration 0001). */
  lat: number | null
  lng: number | null
  /** Horodatage de la dernière position (`drivers.last_seen`). */
  last_seen: string | null
  /** D'où vient la position : l'app livreur, ou un boîtier GPS rattaché (`drivers.position_source`, 0026). Absent = app. */
  position_source?: 'app' | 'boitier'
}

/* ------------------------------------------------------- boîtiers GPS (0026) */

/** `gps_devices` (0026) : un boîtier reçu via Traccar, et sa dernière position. */
export interface Boitier {
  id: string
  /** Identifiant du boîtier dans Traccar (`device.uniqueId`, souvent l'IMEI). */
  unique_id: string
  label: string | null
  vehicle_id: string | null
  driver_id: string | null
  active: boolean
  last_lat: number | null
  last_lng: number | null
  last_fix_at: string | null
  last_received_at: string | null
  last_speed_kmh: number | null
  last_course: number | null
}

/** Un véhicule de la flotte, tel que la console en a besoin (rattachement d'un boîtier, étiquette sur la carte). */
export interface VehiculeOps {
  id: string
  plate: string
  model: string
  type: string
}

/* ------------------------------------------------------------------ courses */

/**
 * Décomposition du prix d'une course opérée (`courses.price_breakdown`, 0030), posée par la base.
 * `source` : `grille` (calculé), `override` (dérogation du propriétaire, motif dans `raison`) ou
 * `saisie` (sans coordonnées : prix tel que saisi). Les autres champs sont ceux de `DetailPrix`
 * quand la base a pu calculer.
 */
export interface BilanPrix extends Partial<DetailPrix> {
  source: 'grille' | 'override' | 'saisie'
  raison?: string
  prix_saisi?: number
  sans_coordonnees?: boolean
}

export interface Course {
  id: string
  ref: string
  client_account_id: string
  statut: Statut
  pickup: string
  dropoff: string
  /** Prix TOTAL facturé au client, supplément hors créneau COMPRIS. */
  price_fcfa: number
  surcharge_fcfa: number
  offslot: boolean
  driver_id: string | null
  channel: Canal
  created_at: string
  delivered_at: string | null
  /** Point de livraison géolocalisé (`courses.dropoff_lat/lng`, 0001) — vide tant que la position
      n'est pas connue (épingle, Plus Code, lien envoyé au destinataire). */
  dropoff_lat: number | null
  dropoff_lng: number | null
  /** Point de collecte géolocalisé (`courses.pickup_lat/lng`, 0001). */
  pickup_lat: number | null
  pickup_lng: number | null
  /** Véhicule demandé (0030) : le coefficient de la grille en dépend. */
  requested_vehicle: TypeVehicule | null
  /** Distance de route estimée retenue par la base (km), nulle sans coordonnées. */
  distance_km: number | null
  price_breakdown: BilanPrix | null
  /** Motif de la dérogation du propriétaire, le cas échéant. */
  price_override_reason: string | null
  /** Téléphone saisi à la commande (`courses.client_phone`, 0001) : sert aux messages WhatsApp. */
  client_phone: string | null
  /** Destinataire (0028), distinct du client quand il est renseigné : nom et téléphone facultatifs. `null` = le client reçoit lui-même. */
  recipient_name: string | null
  recipient_phone: string | null
  /* Preuve de livraison — migration 0021. */
  proof_photo_path: string | null
  proof_recipient_name: string | null
  proof_lat: number | null
  proof_lng: number | null
  delivery_code: string | null
  /** Course de l'app des particuliers (0047) : elle seule a un fil client ↔ livreur, où l'équipe peut écrire (0067). */
  app_particulier: boolean
}

/* ----------------------------------------------------- grille tarifaire (0030) */

/** `tarifs_operes` : la grille de l'organisation opératrice, lue par le personnel, écrite par le propriétaire. */
export interface TarifsOperes extends GrilleOperee {
  option_longue_distance_mensuel_fcfa: number
  /** Frais mobile money en FRACTION du montant (0,015 = 1,5 %). */
  frais_mm_encaissement_wave: number
  frais_mm_encaissement_om: number
  frais_mm_decaissement_wave: number
  frais_mm_decaissement_om: number
  /** Vrai tant que le propriétaire n'a rien validé : les chiffres sont des ordres de grandeur. */
  grille_provisoire: boolean
  updated_at: string | null
  /** Faux si la base n'a pas encore la 0043 (prix minimum, tension) : la console montre alors les valeurs par défaut. */
  bareme_progressif_en_base?: boolean
}

/* ------------------------------------------------------------- organisation */

export interface Organisation {
  name: string
  legal_form?: string | null
  nif?: string | null
  rccm?: string | null
  address?: string | null
  phone?: string | null
  email?: string | null
  /**
   * ⚠️ Assujettir l'organisation change rétroactivement tous les états : le
   * chiffre d'affaires passe hors taxes et une dette envers l'État apparaît au
   * bilan. La taxe est EXTRAITE des montants facturés, jamais ajoutée.
   */
  vat_registered: boolean
  /** Stocké en POURCENTAGE (18), pas en fraction — comme la colonne en base. */
  vat_rate: number
  currency: string
  invoice_prefix: string
  driver_join_code?: string | null
  /** Nom commercial (0004) — sert d'expéditeur aux courriers de relance. */
  brand_name?: string | null
  /** 0021 : une photo est exigée pour passer une course à « livrée »
      (contrôlé par l'edge function `set-course-status`). */
  require_delivery_proof: boolean
}

/* -------------------------------------------------------------- comptabilité */

export interface Depense {
  id: string
  category: string
  label: string
  amount_fcfa: number
  spent_on: string
}

/**
 * Nature d'une ligne de facture (`invoice_lines.kind`, 0032). `course` = facturation à l'unité saisie à la
 * main ; les autres viennent de la facturation mensuelle des clients PME au forfait.
 */
export type NatureLigne =
  | 'course'
  | 'course_incluse'
  | 'forfait'
  | 'option_longue_distance'
  | 'depassement'
  | 'supplement_horaire'
  | 'longue_distance'
  | 'majoration_tension'

export interface LigneFacture {
  course_id: string | null
  description: string
  qty: number
  unit_price_ht_fcfa: number
  amount_ht_fcfa: number
  /** Absent = `course` (lignes d'avant la 0032). */
  kind?: NatureLigne
}

/** Une ligne de facture, réduite à ce que la comptabilité répartit : à quelle facture, de quelle nature, combien HT. */
export interface LigneRepartition {
  invoice_id: string
  kind: NatureLigne
  amount_ht_fcfa: number
}

export type StatutFacture = 'issued' | 'paid' | 'cancelled'

/**
 * Moyens de règlement, tels que STOCKÉS dans `invoices.payment_method` (0023) :
 * minuscules, sans accent. C'est la seule liste que la console écrit.
 */
export type MoyenReglement = 'especes' | 'wave' | 'orange_money' | 'virement' | 'cheque'

/** Une paie de primes « forte demande » (`paies_primes_livreurs`, 0044) : ce que la compta en lit. */
export interface PaiePrimeLivreur {
  id: string
  driver_nom: string
  /** Premier jour du mois payé. */
  mois: string
  montant_fcfa: number
  moyen: 'especes' | 'wave' | 'orange_money' | 'virement'
  reference: string | null
  paye_le: string
}

export interface Facture {
  id: string
  number: string
  client_account_id: string | null
  /**
   * ⚠️ PAS une colonne : `invoices` n'a pas de `client_name` (0003). La
   * version HTML lisait ce champ inexistant — toutes ses factures, son
   * auxiliaire 411 et ses relances étaient au nom de « Client ». Il est
   * reconstitué au chargement depuis `client_account_id`.
   */
  client_name: string
  status: StatutFacture
  issue_date: string
  subtotal_ht_fcfa: number
  vat_rate: number
  vat_fcfa: number
  total_ttc_fcfa: number
  notes: string | null
  /**
   * Date de RÈGLEMENT (0023). Nulle tant que la facture n'est pas réglée : c'est
   * elle — pas le statut — qui éteint la créance et date l'écriture bancaire.
   */
  paid_at: string | null
  payment_method: MoyenReglement | null
  /** Frais mobile money du règlement (Wave / Orange Money), calculés par la base au taux d'alors (0033). */
  frais_mm_fcfa: number
  /** Premier jour du mois facturé, pour une facture mensuelle générée (0032) ; `null` si saisie à la main. */
  periode_mois: string | null
  lines: LigneFacture[]
}

/* ------------------------------------------- forfaits, abonnements, encaissements */

/** `forfaits_operes` (0031) : un forfait PME de l'organisation. `prix_mensuel_fcfa` nul = sur devis ; `quota_courses` nul = illimité. */
export interface Forfait {
  code: string
  libelle: string
  prix_mensuel_fcfa: number | null
  quota_courses: number | null
  actif: boolean
  sort_order: number
}

export type StatutAbonnement = 'active' | 'paused' | 'closed'

/**
 * Une période d'abonnement d'un client PME (`client_subscriptions`, 0031). Prix et quota sont FIGÉS à la
 * souscription. `date_fin` nulle = l'abonnement courant (un seul par client).
 */
export interface Abonnement {
  id: string
  client_account_id: string
  plan_code: string
  status: StatutAbonnement
  prix_mensuel_fcfa: number
  quota_courses: number | null
  sur_devis: boolean
  option_longue_distance: boolean
  option_prix_mensuel_fcfa: number
  date_debut: string
  date_fin: string | null
  jour_facturation: number
}

/** Les quatre moyens d'encaissement d'une course (`course_payments.moyen`, 0033). */
export type MoyenEncaissement = 'wave' | 'orange_money' | 'especes' | 'autre'

/** Un paiement de course ENREGISTRÉ (`course_payments`, 0033). Seuls ceux dont le statut est `succeeded` comptent. */
export interface Encaissement {
  id: string
  course_id: string
  client_account_id: string | null
  moyen: MoyenEncaissement
  amount_fcfa: number
  /** Frais mobile money calculés par la base au taux d'alors (Wave / Orange Money), 0 sinon. */
  frais_fcfa: number
  /** Date et heure du paiement (ISO). */
  paid_at: string
  status: 'succeeded' | 'failed' | 'pending'
  note: string | null
}

/* -------------------------------------------------------------------- équipe */

/**
 * Les QUATRE valeurs de l'énumération `public.member_role` (0001). Il n'en
 * existe pas d'autre : l'ancien type ajoutait un rôle `admin` qui n'a jamais
 * existé en base.
 */
export type RoleMembre = 'owner' | 'dispatcher' | 'accountant' | 'driver'

export interface Membre {
  id: string
  full_name: string
  /** Vide pour un compte à numéro seul. */
  email: string
  /** E.164, vide pour un compte à e-mail seul. */
  phone: string
  role: RoleMembre
  status: string
  permissions: Record<string, boolean>
  title: string | null
}

/* --------------------------------------------------------------- messagerie */

export interface Message {
  id: string
  driver_id: string
  sender: 'driver' | 'dispatch'
  body: string
  created_at: string
  read_at: string | null
}

/* ---------------------------------------------------------- état de la console */

export interface DonneesOps {
  /** Les clients ACTIFS ET EN PAUSE : ceux qu'on propose à la prise d'appel. Les clos n'y sont pas. */
  clients: Client[]
  /**
   * TOUS les clients, clos compris : une facture ou une course ancienne doit garder le nom et le type
   * de son client dans les livres. La compta lit cette liste ; la prise d'appel ne la lit pas.
   */
  tousClients: Client[]
  drivers: Livreur[]
  courses: Course[]
  org: Organisation
  expenses: Depense[]
  members: Membre[]
  invoices: Facture[]
  /**
   * Les courses DÉJÀ facturées. Sans cet ensemble, l'écran de facturation les
   * reproposait pré-cochées et le client payait deux fois. Une facture annulée
   * n'y figure pas — sa course redevient facturable.
   */
  billed: Set<string>
  messages: Message[]
  /** Conversations avec les clients (0067), la plus récente d'abord ; vide sans droit ou avant la migration. */
  conversationsClients: ConversationClient[]
  /**
   * TOUTES les courses livrées (paginées), pour la comptabilité, la
   * facturation et l'historique. `courses` ne couvre que la fenêtre du
   * dispatch (actives + 24 h) : la compta HTML en tirait son chiffre
   * d'affaires, qui ne comptait donc que la dernière journée.
   */
  livrees: Course[]
  /** Écritures manuelles — `org_documents`, clé `ecritures_manuelles`. */
  ecrituresManuelles: EcritureManuelle[]
  /** Libellés de comptes renommés ou ajoutés — `org_documents`, clé `plan_comptable`. */
  planComptable: Record<string, string>
  /** Archives de clôture (`accounting_closures`, 0008, ajout seul). */
  clotures: Cloture[]
  /** Piste d'audit (`accounting_closure_events`, 0008, ajout seul). */
  evenementsCloture: EvenementCloture[]
  /** Boîtiers GPS rattachés à l'organisation (`gps_devices`, 0026). */
  boitiers: Boitier[]
  /** Véhicules de la flotte (rattachement des boîtiers, étiquettes de la carte). */
  vehicules: VehiculeOps[]
  /** Forfaits PME de l'organisation (0031) — vide si illisibles (migration pas encore poussée). */
  forfaits: Forfait[]
  /** Toutes les périodes d'abonnement des clients PME (0031), historique compris. */
  abonnements: Abonnement[]
  /** Paiements de courses enregistrés (0033), annulés compris (on filtre sur `status`). */
  encaissements: Encaissement[]
  /** Primes de forte demande payées aux livreurs (0044) — vide si illisibles (rôle, migration absente). */
  paiesPrimes: PaiePrimeLivreur[]
  /** Lignes de toutes les factures non brouillon (nature + HT) : répartition du 7061 par nature. */
  lignesFactures: LigneRepartition[]
  /**
   * « Encaissé aujourd'hui », calculé par la base (RPC `encaisse_du_jour`) : paiements ENREGISTRÉS du jour,
   * règlements de factures compris. `null` si la RPC est illisible.
   */
  encaisseJour: number | null
  /** Grille tarifaire opérée (0030) ; `null` si illisible ou migration pas encore poussée : la prise
      d'appel retombe alors sur la saisie du prix, et le dit. */
  tarifs: TarifsOperes | null
  /** La lecture des registres a-t-elle abouti ? Sinon on le DIT à l'écran. */
  synchro: {
    documents: boolean
    clotures: boolean
    reglements: boolean
    boitiers: boolean
    /** Abonnements et forfaits lisibles (0031) ? */
    abonnements: boolean
    /** Encaissements de courses lisibles (0033) ? Sinon les créances de particuliers sont surévaluées. */
    encaissements: boolean
  }
}

/* ------------------------------------------------------- écritures manuelles */

export type Journal = 'VE' | 'AC' | 'BQ' | 'CA' | 'OD'

/**
 * Une écriture manuelle en partie double : UN compte débité, UN compte
 * crédité, UN montant. Équilibrée par construction.
 */
export interface EcritureManuelle {
  id: string
  /** Date comptable, `AAAA-MM-JJ`. */
  date: string
  piece: string
  journal: Journal
  libelle: string
  debit: string
  credit: string
  montant: number
  cree_le: string
  cree_par: string | null
}

/* ----------------------------------------------------------------- clôture */

export interface LigneBalanceArchivee {
  code: string
  label: string
  debit: number
  credit: number
  solde: number
}

export interface Cloture {
  id: string
  annee: number
  /** `AAAA-12-31` */
  dateVerrou: string
  clotureLe: string
  resultat: number
  produits: number
  charges: number
  nbComptes: number
  balance: LigneBalanceArchivee[]
  empreinte: string | null
}

export interface EvenementCloture {
  id: string
  annee: number
  action: 'cloture' | 'reouverture'
  detail: string
  le: string
}
